/**
 * The rules behind the Requests tab (masterplan credit-limits-v3, B4; E5, E6, E10, E11, E33). Pure, so the tests read
 * them without a browser; `RequestsTab` and `RequestDialog` only draw what this file decides.
 *
 * - What a person may do comes from the API (`allowedActions` of the DTO, `workflow.ts`); the tab never guesses rights.
 *   A button exists exactly for each allowed action.
 * - A reason is required for an approval on behalf (E5) and for a rejection after an approval, i.e. an overruling
 *   (E10, E33): the API flags both with `commentRequired`, and an `onBehalf` approval is treated as required even if a
 *   flag were missing. The server checks again (400 `COMMENT_REQUIRED`).
 * - «Waiting for me»: pending requests the person can approve in his own name (not merely on behalf). A Finance step
 *   waits for every Finance person.
 * - The deep link of the request mails is `?tab=requests&request=<id>` (`request-mail.ts`).
 */
import type { AllowedAction, CreditLimitRequestDto, NavAction, RequestAction, RequestStatus } from '../../../types/creditLimits';
import { nameFromEmail, ROLE_LABEL } from './chainPreview';

/** Reason and comment columns are NVARCHAR(1000). */
export const MAX_COMMENT_LENGTH = 1000;
export const REQUEST_PARAM = 'request';
export const WAITING_PARAM = 'waiting';

export const STATUS_LABEL: Record<RequestStatus, string> = {
    pending: 'In approval',
    ready: 'Ready for NAV',
    writing: 'Writing to NAV',
    written: 'Written to NAV',
    manual: 'Entered in NAV by hand',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
};

/** Who decides next, in words: «Wolfgang Gomm (BT manager)», «Finance (Stefan Boog or the other Finance person)». */
export function nextApproverText(r: Pick<CreditLimitRequestDto, 'status' | 'nextApproverEmail' | 'nextApproverRole'>): string | null {
    if (r.status !== 'pending' || !r.nextApproverEmail || !r.nextApproverRole) return null;
    if (r.nextApproverRole === 'FIN') return `Finance (${nameFromEmail(r.nextApproverEmail)} or another Finance person)`;
    return `${nameFromEmail(r.nextApproverEmail)} (${ROLE_LABEL[r.nextApproverRole]})`;
}

/**
 * The status column: «In approval · next: Bruno Crippa (top level)», «Ready for NAV». After the NAV step it says
 * «Written to NAV» or «Entered in NAV by hand», then «confirmed by the nightly run» once the DWH carries the new limit.
 */
export function statusText(r: Pick<CreditLimitRequestDto, 'status' | 'nextApproverEmail' | 'nextApproverRole'> & { nav?: Pick<CreditLimitRequestDto['nav'], 'confirmedByNightlyRun'> }): string {
    const label = STATUS_LABEL[r.status] ?? r.status;
    const next = nextApproverText(r);
    if (next) return `${label} · next: ${next}`;
    if ((r.status === 'written' || r.status === 'manual') && r.nav?.confirmedByNightlyRun) return `${label} · confirmed by the nightly run`;
    return label;
}

/**
 * The person has something to do: pending and he can approve in his own name (E6), or ready for NAV and the API
 * offers him the write (Finance, 2026-10-23).
 */
export function isWaitingForMe(r: Pick<CreditLimitRequestDto, 'status' | 'allowedActions'>): boolean {
    if (r.status === 'ready') return r.allowedActions.some(a => a.action === 'nav_write');
    return isPendingApprover(r);
}

/** The person is the one the pending approval waits for (approve in his own name). Only this decides «Overrule». */
function isPendingApprover(r: Pick<CreditLimitRequestDto, 'status' | 'allowedActions'>): boolean {
    return r.status === 'pending' && r.allowedActions.some(a => a.action === 'approve' && !a.onBehalf);
}

/**
 * An amount as it is written: whole francs without decimals, with cents when there are any («12'345.50»). Never rounds
 * what the app writes into NAV. A missing value is a dash.
 */
export function formatChfExact(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '–';
    const cents = Math.round(value * 100);
    const hasCents = cents % 100 !== 0;
    return (cents / 100).toLocaleString('de-CH', { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: hasCents ? 2 : 0 });
}

export function isCommentRequired(a: AllowedAction): boolean {
    return a.commentRequired || (a.action === 'approve' && a.onBehalf);
}

export interface ActionButton {
    action: RequestAction | NavAction;
    onBehalf: boolean;
    commentRequired: boolean;
    /** The confirmation has no comment field (NAV write and check). */
    noComment?: boolean;
    label: string;
    /** Longer text for the confirmation: what happens and who learns of it. */
    explain: string;
    tone: 'primary' | 'secondary';
}

const ORDER: Record<RequestAction | NavAction, number> = { approve: 0, reject: 1, withdraw: 2, nav_write: 3, nav_check: 4, nav_manual: 5 };

const chf = (v: number) => `CHF ${formatChfExact(v)}`;

type ButtonSource = Pick<CreditLimitRequestDto, 'status' | 'allowedActions' | 'nextApproverEmail' | 'steps'>
    & Partial<Pick<CreditLimitRequestDto, 'customerName' | 'customerCode' | 'currentLimitChf' | 'requestedLimitChf'>>;

/** The confirmation text of «Write to NAV»: customer, old and new limit (C2). */
function navWriteExplain(r: ButtonSource): string {
    const who = r.customerName?.trim() || r.customerCode || 'the customer';
    const amounts = typeof r.currentLimitChf === 'number' && typeof r.requestedLimitChf === 'number'
        ? `: ${chf(r.currentLimitChf)} → ${chf(r.requestedLimitChf)}` : '';
    return `The app writes the credit limit of ${who}${amounts} into NAV, live. NAV shows the user dolder as the changer. `
        + 'The app reads the value back and the nightly run confirms it.';
}

/**
 * One button per allowed action, in a fixed order: approve, reject, withdraw, then the NAV actions. The NAV buttons come
 * from the API like the others (`nav_write`, `nav_check`, `nav_manual`); while the request is `writing` the write button
 * gives way to «Check NAV».
 */
export function actionButtons(r: ButtonSource): ActionButton[] {
    // E10: a rejection after an approval, by anyone but the person the step waits for, overrules that approval.
    // Derived from the pending approval only: Finance waiting at `ready` (nav_write) does not change the label.
    const overrules = r.steps.some(s => s.decision === 'approved') && !isPendingApprover(r);
    return [...r.allowedActions]
        .filter(a => !(a.action === 'nav_write' && r.status === 'writing'))
        .sort((a, b) => ORDER[a.action] - ORDER[b.action])
        .map((a): ActionButton => {
            if (a.action === 'nav_write') {
                return { action: a.action, onBehalf: false, commentRequired: false, noComment: true, tone: 'primary', label: 'Write to NAV', explain: navWriteExplain(r) };
            }
            if (a.action === 'nav_check') {
                return {
                    action: a.action, onBehalf: false, commentRequired: false, noComment: true, tone: 'primary', label: 'Check NAV',
                    explain: 'The app reads the credit limit in NAV and compares it with the requested value. The earlier write did not report back.',
                };
            }
            if (a.action === 'nav_manual') {
                return {
                    action: a.action, onBehalf: false, commentRequired: false, tone: 'secondary', label: 'Entered in NAV by hand',
                    explain: 'Confirm only after the limit was entered in NAV by hand (for example because NAV refused the write). The request is then closed. A comment is optional.',
                };
            }
            const commentRequired = isCommentRequired(a);
            if (a.action === 'approve') {
                return a.onBehalf
                    ? {
                        action: a.action, onBehalf: true, commentRequired, tone: 'secondary' as const,
                        label: 'Approve on behalf',
                        explain: `You approve on behalf of ${nameFromEmail(r.nextApproverEmail)}. A reason is required; ${nameFromEmail(r.nextApproverEmail)} and Roman Dill get a mail.`,
                    }
                    : { action: a.action, onBehalf: false, commentRequired, tone: 'primary' as const, label: 'Approve', explain: 'You approve this step. A comment is optional.' };
            }
            if (a.action === 'reject') {
                return {
                    action: a.action, onBehalf: false, commentRequired, tone: 'secondary' as const,
                    label: overrules ? 'Overrule' : 'Reject',
                    explain: overrules
                        ? 'The request was already approved at least once: rejecting it now overrules that approval. A reason is required.'
                        : `You reject the request. ${commentRequired ? 'A reason is required.' : 'A comment is optional.'}`,
                };
            }
            return { action: a.action, onBehalf: false, commentRequired, tone: 'secondary' as const, label: 'Withdraw', explain: 'You withdraw your request. The cell can be changed again afterwards.' };
        });
}

/** null when the comment may be sent, else the message to show. */
export function commentError(button: Pick<ActionButton, 'commentRequired'>, comment: string): string | null {
    const t = comment.trim();
    if (button.commentRequired && t === '') return 'A reason is required.';
    if (t.length > MAX_COMMENT_LENGTH) return `At most ${MAX_COMMENT_LENGTH} characters.`;
    return null;
}

/** The request id of `?request=<id>`; null when missing or not a positive whole number. */
export function requestIdFromParams(params: URLSearchParams): number | null {
    const raw = params.get(REQUEST_PARAM);
    if (raw === null || !/^\d{1,10}$/.test(raw)) return null;
    const id = Number(raw);
    return id > 0 ? id : null;
}

/** The newest first; a filter on «waiting for me». */
export function visibleRequests(list: readonly CreditLimitRequestDto[], waitingOnly: boolean): CreditLimitRequestDto[] {
    const rows = waitingOnly ? list.filter(isWaitingForMe) : [...list];
    return rows.sort((a, b) => b.requestedAt.localeCompare(a.requestedAt) || b.id - a.id);
}

/** Replace one request in the list by the action's answer (or add it). */
export function replaceRequest(list: readonly CreditLimitRequestDto[], next: CreditLimitRequestDto): CreditLimitRequestDto[] {
    const i = list.findIndex(r => r.id === next.id);
    if (i < 0) return [next, ...list];
    const copy = [...list];
    copy[i] = next;
    return copy;
}

/** «23.10.2026 14:05» in Swiss local time; a dash when missing. */
export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '–';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '–';
    return d.toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Zurich' });
}

/** A failed call as a sentence, with the HTTP status where it tells the person something. */
export function apiErrorText(err: unknown, fallback: string): string {
    const e = err as { message?: string; status?: number } | null;
    const msg = e && typeof e.message === 'string' && e.message ? e.message : fallback;
    if (e?.status === 403) return `Not allowed: ${msg}`;
    if (e?.status === 423) return `NAV writing is locked: ${msg} Finance can unlock it in the Approval rules tab, after checking the password of the NAV user dolder.`;
    if (e?.status === 409) return `Conflict: ${msg} Reload to see the current state.`;
    return msg;
}
